import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'
import { LINK_TYPES, RECORD_SEARCH, linkText, linkOpen, matchRecords, typeOfTable, niceDate } from '../lib/ismLinks'

// ISM evidence: linked records (checklist run, crew certificate, drill, document, crew / training record, crew log, To Do) as
// clickable chips + a "Link record" picker. The picker searches with the login's own rights; the database fills in the
// label and shows a link only to logins that may read the record.
async function vesselRows(cid, table) {
  const S = RECORD_SEARCH[table]
  if (table === 'todo_items') {
    const { data: lists } = await supabase.from('todo_lists').select('id').eq('client_id', cid)
    const ids = (lists || []).map(l => l.id); if (!ids.length) return []
    const { data } = await supabase.from('todo_items').select(S.select).in('list_id', ids).order('created_at', { ascending: false }).limit(500)
    return data || []
  }
  const { data, error } = await supabase.from(table).select(S.select).eq('client_id', cid).order(S.order, { ascending: false }).limit(500)
  if (error) throw new Error(error.message)
  return data || []
}

export default function IsmEvidenceLinks({ cid, findingId, itemId, links, canEdit, onChanged, onNavigate, compact }) {
  const [pick, setPick] = useState(null)   // { type, q, rows, ctx, busy, err }
  const [err, setErr] = useState('')
  const mine = (links || []).filter(l => (findingId ? l.finding_id === findingId : l.item_id === itemId))

  async function chooseType(key) {
    const t = LINK_TYPES.find(x => x.key === key)
    setPick(p => ({ ...p, type: key, rows: null, err: '', busy: true }))
    try {
      const rows = await vesselRows(cid, t.table)
      const ctx = {}
      if (t.table === 'checklist_instances') {
        const { data } = await supabase.from('checklists').select('id,title').eq('client_id', cid).limit(1000)
        ctx.checklistTitle = Object.fromEntries((data || []).map(c => [c.id, c.title]))
      }
      setPick(p => ({ ...p, rows, ctx, busy: false }))
    } catch (e) { setPick(p => ({ ...p, rows: [], busy: false, err: 'Not available with your rights (' + e.message + ')' })) }
  }
  async function link(r) {
    const t = LINK_TYPES.find(x => x.key === pick.type)
    setPick(p => ({ ...p, busy: true, err: '' }))
    const { error } = await supabase.from('ism_evidence_links').insert({ client_id: cid, finding_id: findingId || null, item_id: findingId ? null : itemId, target_table: t.table, target_id: r.id })
    if (error) return setPick(p => ({ ...p, busy: false, err: /Already linked/.test(error.message) ? 'Already linked.' : error.message }))
    setPick(null); onChanged && onChanged()
  }
  async function unlink(l) {
    if (!window.confirm('Remove the link to "' + linkText(l, false) + '"? The record itself stays. You can undo it for 10 seconds.')) return
    setErr('')
    try { await deleteWithUndo(supabase, { table: 'ism_evidence_links', id: l.id, label: 'link to "' + linkText(l, false) + '"', onUndone: () => onChanged && onChanged() }); onChanged && onChanged() }
    catch (e) { setErr(e.message || String(e)) }
  }
  function open(l) {
    const o = linkOpen(l); if (!o || !onNavigate) return
    try { for (const [k, v] of Object.entries(o.keys)) localStorage.setItem(k, v) } catch (e) {}
    onNavigate(o.page)
  }

  const t = pick && LINK_TYPES.find(x => x.key === pick.type)
  const hits = pick && pick.rows ? matchRecords(t.table, pick.rows, pick.q || '', pick.ctx).slice(0, 25) : []
  const chipS = { display: 'inline-flex', alignItems: 'center', gap: 5, border: '0.5px solid var(--border)', borderRadius: 8, padding: '3px 8px', fontSize: 11.5, maxWidth: '100%', background: 'rgba(201,168,76,.07)' }
  return (
    <>
      {(mine.length > 0 || canEdit) && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6, alignItems: 'center' }}>
          {mine.map(l => (
            <span key={l.id} style={chipS} title={(typeOfTable(l.target_table) || {}).label + (l.created_by_name ? ' · linked by ' + l.created_by_name : '')}>
              <button onClick={() => open(l)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--gold2)', cursor: onNavigate ? 'pointer' : 'default', fontSize: 11.5, textAlign: 'left' }}>🔗 {linkText(l)}</button>
              {canEdit && <button onClick={() => unlink(l)} aria-label="Remove link" style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14, padding: 0 }}>×</button>}
            </span>
          ))}
          {canEdit && <button className="btn" style={{ fontSize: 12, padding: compact ? '4px 10px' : undefined }} onClick={() => setPick({ type: '', q: '', rows: null })}>🔗 Link record</button>}
        </div>
      )}
      {err && <div style={{ color: '#C93B47', fontSize: 12, marginTop: 4 }}>{err}</div>}
      {pick && (
        <div onClick={e => { if (e.target === e.currentTarget) setPick(null) }} style={{ position: 'fixed', inset: 0, background: 'rgba(4,10,20,.7)', zIndex: 3200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--ink2)', border: '0.5px solid var(--border2)', borderRadius: 14, width: '100%', maxWidth: 520, maxHeight: '86vh', display: 'flex', flexDirection: 'column', padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <b style={{ fontSize: 14 }}>Link a record as evidence</b>
              <button onClick={() => setPick(null)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14 }}>✕</button>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {LINK_TYPES.map(x => <button key={x.key} className={'btn' + (pick.type === x.key ? ' btn-primary' : '')} style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => chooseType(x.key)}>{x.label}</button>)}
            </div>
            {pick.type && <input autoFocus placeholder={'Search ' + t.label.toLowerCase() + ' (e.g. familiarisation Mia Torres)'} value={pick.q} onChange={e => setPick(p => ({ ...p, q: e.target.value }))}
              style={{ padding: '8px 10px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'var(--ink)', color: 'var(--text)', fontSize: 13, marginBottom: 8 }} />}
            <div style={{ overflowY: 'auto', minHeight: 60 }}>
              {!pick.type && <div style={{ color: 'var(--muted)', fontSize: 12.5 }}>Choose what to link. You see only records your login may open.</div>}
              {pick.busy && <div style={{ color: 'var(--muted)', fontSize: 12.5 }}>Loading&hellip;</div>}
              {pick.type && pick.rows && !pick.busy && !hits.length && <div style={{ color: 'var(--muted)', fontSize: 12.5 }}>Nothing found.</div>}
              {hits.map(h => (
                <div key={h.id} onClick={() => !pick.busy && link(h)} style={{ padding: '7px 8px', borderRadius: 8, cursor: 'pointer', borderBottom: '0.5px solid var(--border)', fontSize: 12.5 }}>
                  {h.label}{h.date ? <span style={{ color: 'var(--muted)' }}> · {niceDate(h.date)}</span> : null}
                </div>
              ))}
            </div>
            {pick.err && <div style={{ color: '#C93B47', fontSize: 12, marginTop: 6 }}>{pick.err}</div>}
          </div>
        </div>
      )}
    </>
  )
}
